import axios from "axios";
import { baseURL } from "../Constants";

const url = `${baseURL}/PowerStrikle`;

export interface PowerStriklePlayer {
    steamId: string;
    playerName: string;
    currentTeam: string;
    age: number;
    seasonsPlayed: number;
    highestDivision: string;
}

export interface PowerStrikleGuess {
    steamId: string;
    playerName: string;
    team: string;
    age: number;
    seasonsPlayed: number;
    highestDivision: string;
}

export interface PowerStrikleComparison {
    playerName: GuessStatus;
    team: GuessStatus;
    age: GuessStatus;
    seasonsPlayed: GuessStatus;
    highestDivision: GuessStatus;
    
    // Directional hints for a miss: "up" when the answer's value is higher (a higher division tier
    // for highestDivision), "down" when lower. The server never sends the answer's values themselves.
    ageHint?: HintDirection | null;
    seasonsPlayedHint?: HintDirection | null;
    highestDivisionHint?: HintDirection | null;
}

export type HintDirection = "up" | "down";

export enum GuessStatus {
    Correct = 0,      // Green - exact match
    Close = 1,        // Orange - close but not exact  
    SameDivision = 2, // Blue - same division level but different subdivision
    Wrong = 3         // Red - completely wrong
}

export interface PowerStrikleGuessResult {
    guess: PowerStrikleGuess;
    comparison: PowerStrikleComparison;
    isCorrect: boolean;
    guessNumber: number;
}

export interface PowerStrikleGameState {
    date: string;
    guesses: PowerStrikleGuessResult[];
    isComplete: boolean;
    isWon: boolean;
    maxGuesses: number;
}

export interface DailyGameResponse {
    date: string;
    gameState: PowerStrikleGameState;
    correctPlayer?: PowerStriklePlayer;
    nextGameDate: string;
    secondsUntilNextGame: number;
    activeSeason: number;
    divisionGroup: string;
}

export interface GuessResponse {
    guessResult: PowerStrikleGuessResult;
    gameState: PowerStrikleGameState;
    correctPlayer?: PowerStriklePlayer;
}

export const getDailyGame = async (token: string, divisionGroup: string = "1div"): Promise<DailyGameResponse | null> => {
    try {
        const response = await axios.get(`${url}/daily`, {
            params: { divisionGroup },
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error fetching daily game:", error);
        return null;
    }
};

export const submitGuess = async (token: string, steamId: string, divisionGroup: string = "1div"): Promise<GuessResponse | null> => {
    try {
        const response = await axios.post(`${url}/guess?divisionGroup=${encodeURIComponent(divisionGroup)}`, {
            steamId
        }, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error("Error submitting guess:", error);
        return null;
    }
};

export const searchPlayers = async (query: string, divisionGroup?: string, season?: number): Promise<PowerStriklePlayer[]> => {
    try {
        if (query.length < 2) return [];
        const params: any = { query };
        if (divisionGroup) params.divisionGroup = divisionGroup;
        if (season) params.season = season;
        
        const response = await axios.get(`${url}/players/search`, { params });
        return response.data;
    } catch (error) {
        console.error("Error searching players:", error);
        return [];
    }
};